import React, { useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getCountryForUi } from "../../store/reducer/country/countrySlice";
import { addCustomer } from "../../store/reducer/customers/customersSlice";
import { addCustomerSite } from "../../store/reducer/customers/customerSiteSlice";
import { addCustomerContact } from "../../store/reducer/customers/customerContactSlice";

function AddCustomer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { options = [] } = useSelector((state) => state.ddOptions);
  const { countries = [] } = useSelector((state) => state.countries);

  useEffect(() => {
    dispatch(getDdOptionForUI());
    dispatch(getCountryForUi());
  }, [dispatch]);

  const validationSchema = Yup.object().shape({
    customer_name: Yup.string().required("please enter customer name"),
    trading_name: Yup.string().required("please enter trading name"),
    contact_person: Yup.string().required("please enter contact person name"),
    email: Yup.string().email("Invalid email address").required("please enter email"),
    customer_type: Yup.string().required("please select customer type"),
    industry: Yup.string().required("please select industry"),
  });

  const initialValues = {
    customer_name: "",
    trading_name: "",
    contact_person: "",
    email: "",
    phone: "",
    customer_type: "",
    industry: "",
    street: "",
    city: "",
    state: "",
    post_code: "",
    country: "",
    status: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    values.createdBy = JSON.parse(localStorage.getItem("userSession")).id;

    const formData = new FormData();
    Object.keys(values).forEach((field) => formData.append(field, values[field]));

    await dispatch(addCustomer(formData)).then((res) => {
      if (!res.payload) return toast.error(res.error.message);

      const { id, uniqueId, ...cust } = res.payload.result;

      const siteData = {
        customer_id: id,
        customer_uniqueId: uniqueId,
        site_name: cust.customer_name,
        street: cust.street,
        city: cust.city,
        state: cust.state,
        post_code: cust.post_code,
        country: cust.country,
        status: cust.status,
        createdBy: values.createdBy,
      };

      const [firstName, ...rest] = cust.contact_person.split(" ");
      const contactData = {
        customer_id: id,
        customer_uniqueId: uniqueId,
        is_primary: true,
        firstname: firstName,
        lastname: rest.join(" "),
        phone: cust.phone,
        email: cust.email,
        status: cust.status,
        createdBy: values.createdBy,
      };

      dispatch(addCustomerContact(contactData)).then((cRes) => {
        if (!cRes.payload) return toast.error(cRes.error.message);

        siteData.customer_contact_id = cRes.payload.result.id;
        dispatch(addCustomerSite(siteData)).then((sRes) => {
          if (!sRes.payload) return toast.error(sRes.error.message);

          toast.success(sRes.payload.message);
          navigate(`/customer/${id}`);
        });
      });
    });
  };

  return (
    <>
      {/* Header */}
      <div className="flex justify-between mb-8">
        <div className="flex gap-4 items-center">
          <Link to="/customers">
            <div className="bg-[#F8F8F8] rounded-[10px] p-2">
              <img
                className="w-6"
                src={`${process.env.PUBLIC_URL}/icons/back.png`}
                alt="back icon"
              />
            </div>
          </Link>
          <div>
            <p className="text-sm leading-4 text-[#9E9E9E]">Customer / Add</p>
            <h1 className="text-2xl font-bold leading-8">Add Customer</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, setFieldValue }) => (
          <Form>
            {/* 3-Column Grid */}
            <div className="bg-[#F8F8F8] p-6 rounded-2xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* -------- Column 1 -------- */}
                <div className="flex flex-col gap-4">
                  <FieldWrapper
                    label="Customer Name"
                    name="customer_name"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="Trading Name"
                    name="trading_name"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="Street"
                    name="street"
                    as="textarea"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="City"
                    name="city"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="State"
                    name="state"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="Post Code"
                    name="post_code"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <SelectWrapper
                    label="Country"
                    name="country"
                    options={countries.map((c) => ({
                      value: c.id,
                      label: c.short_name,
                    }))}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                </div>

                {/* -------- Column 2 -------- */}
                <div className="flex flex-col gap-4">
                  <FieldWrapper
                    label="Contact Person"
                    name="contact_person"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="Email"
                    name="email"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <FieldWrapper
                    label="Phone"
                    name="phone"
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />

                  <label className="text-[15px] text-[#9E9E9E]">Status</label>
                  <div>
                    <label className="inline-flex items-center px-5 py-2 bg-white rounded-[12px] text-gray-800">
                      <input
                        type="checkbox"
                        className="hidden peer"
                        name="status"
                        onChange={(e) =>
                          setFieldValue("status", e.target.checked ? "Active" : "Inactive")
                        }
                      />
                      <span className="px-4 py-1 border border-gray-600 rounded-lg peer-checked:border-0">
                        Inactive
                      </span>
                      <span className="px-4 py-1 rounded-lg border border-transparent peer-checked:border-gray-600">
                        Active
                      </span>
                    </label>
                  </div>
                </div>

                {/* -------- Column 3 -------- */}
                <div className="flex flex-col gap-4">
                  <SelectWrapper
                    label="Customer Type"
                    name="customer_type"
                    options={options
                      .filter(
                        (o) => o.ui_name === "Customer" && o.field_name === "customer_type"
                      )
                      .sort((a, b) => a.sort_field - b.sort_field)
                      .map((o) => ({
                        value: o.id,
                        label: `${o.field_code} ${o.field_value}`,
                      }))}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                  <SelectWrapper
                    label="Customer Industry"
                    name="industry"
                    options={options
                      .filter(
                        (o) => o.ui_name === "Customer" && o.field_name === "industry"
                      )
                      .sort((a, b) => a.sort_field - b.sort_field)
                      .map((o) => ({ value: o.id, label: o.field_code }))}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-[#F8F8F8] mt-5 p-4 rounded-[12px]">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full md:w-auto py-3 px-12 rounded-[12px] text-lg text-white ${
                  isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                }`}
              >
                {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

/* ---------- helpers ---------- */

const FieldWrapper = ({
  label,
  name,
  errors,
  touched,
  as = "input",
  setFieldValue,
}) => (
  <div>
    <label htmlFor={name} className="text-[15px] text-[#9E9E9E]">
      {label}
    </label>
    <Field
      as={as}
      id={name}
      name={name}
      className="w-full bg-white border border-gray-200 rounded-[12px] py-3 px-4 leading-tight focus:outline-none focus:border-gray-500"
      onChange={(e) => setFieldValue(name, e.target.value)}
    />
    {errors?.[name] && touched?.[name] && (
      <p className="text-red-700">{errors[name]}</p>
    )}
  </div>
);

const SelectWrapper = ({
  label,
  name,
  options,
  errors,
  touched,
  setFieldValue,
}) => (
  <div>
    <label htmlFor={name} className="text-[15px] text-[#9E9E9E]">
      {label}
    </label>
    <div className="relative">
      <Field
        as="select"
        id={name}
        name={name}
        className="w-full bg-white border border-gray-200 rounded-[12px] py-3 px-4 pr-12 leading-tight focus:outline-none focus:border-gray-500"
        onChange={(e) => setFieldValue(name, e.target.value)}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Field>
    </div>
    {errors?.[name] && touched?.[name] && (
      <p className="text-red-700">{errors[name]}</p>
    )}
  </div>
);

export default AddCustomer;
